//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { IScope } from 'angular';
import React from 'react';
import styled from 'styled-components';

import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { BodySecondaryText, SectionText } from '@components/atoms/text/Text';
import { TrackingId } from '@models/TrackingId';
import makeAngularReactControllerWithScope, {
  applyScope,
} from '@react-bridge/angular-react-widget-with-scope';
import { UIATrackingIds } from '@uia/constants/uiaConstants';
import { withTracking } from '@utils/withTracking';

import AddToLibraryImg from 'images/uia-add-to-library.svg';
import ApproveForFanreelImg from 'images/uia-approve-for-fanreel.svg';
import OnePageImg from 'images/uia-one-page.svg';
import RevealImg from 'images/uia-reveal-it.svg';
import ScheduleNowImg from 'images/uia-schedule-now.svg';
import ShowroomImg from 'images/uia-showroom.svg';

interface UIADistributeProps {
  outstandingAsyncAction: string | null;
  isSocialMediaOptionEnabled: boolean;
  isApproveForFanreelOptionEnabled: boolean;
  isApproveForGalleriesOptionEnabled: boolean;
  isApproveForSyndicationOptionEnabled: boolean;
  isOnePageOptionEnabled: boolean;
  isRevealOptionEnabled: boolean;
  isShowroomOptionEnabled: boolean;
  isMediaLibraryOptionEnabled: boolean;
  activateWorkflow: (workflow: string) => void;
  addToLibrary: (event: React.MouseEvent) => void;
  launchOnePageWorkflow: () => void;
  launchShowroomWorkflow: () => void;
  launchToView: (view: string, workflow: string) => void;
}

export const UIADistribute = ({
  activateWorkflow,
  addToLibrary,
  isApproveForFanreelOptionEnabled,
  isApproveForGalleriesOptionEnabled,
  isApproveForSyndicationOptionEnabled,
  isMediaLibraryOptionEnabled,
  isOnePageOptionEnabled,
  isRevealOptionEnabled,
  isShowroomOptionEnabled,
  isSocialMediaOptionEnabled,
  launchOnePageWorkflow,
  launchShowroomWorkflow,
  launchToView,
  outstandingAsyncAction,
}: UIADistributeProps) => {
  return (
    <section>
      {outstandingAsyncAction ? (
        <SpinnerContainer>
          <CenteredSpinner />
        </SpinnerContainer>
      ) : (
        <List>
          {isSocialMediaOptionEnabled && (
            <UIADistributeOption
              description="Grow your community and amplify your brand's reach"
              handleClick={(_e) => activateWorkflow('ScheduleNow')}
              imageSrc={ScheduleNowImg}
              title='Social Media'
              trackingId={UIATrackingIds.DISTRIBUTION_SOCIAL_MEDIA_OPTION}
            />
          )}

          {isApproveForFanreelOptionEnabled && (
            <UIADistributeOption
              description='Boost conversions on site with inspirational UGC media'
              handleClick={(_e) => activateWorkflow('ApproveForFanreel')}
              imageSrc={ApproveForFanreelImg}
              title='Onsite Galleries'
              trackingId={
                UIATrackingIds.DISTRIBUTION_APPROVE_FOR_FANREEL_OPTION
              }
            />
          )}

          {isApproveForGalleriesOptionEnabled && (
            <UIADistributeOption
              description='Boost conversions on site with inspirational UGC media'
              handleClick={(_e) => activateWorkflow('ApproveForGalleries')}
              imageSrc={ApproveForFanreelImg}
              title='Onsite Galleries'
              trackingId={
                UIATrackingIds.DISTRIBUTION_APPROVE_FOR_GALLERIES_OPTION
              }
            />
          )}

          {isApproveForSyndicationOptionEnabled && (
            <UIADistributeOption
              description='Drive retail growth with impactful visual content'
              handleClick={(_e) => activateWorkflow('ApproveForGalleries')}
              imageSrc={ApproveForFanreelImg}
              title='Approve for Syndication'
              trackingId={
                UIATrackingIds.DISTRIBUTION_APPROVE_FOR_SYNDICATION_OPTION
              }
            />
          )}

          {isOnePageOptionEnabled && (
            <UIADistributeOption
              description='Drive traffic to site with dynamic landing pages'
              handleClick={(_e) => launchOnePageWorkflow()}
              imageSrc={OnePageImg}
              title='ONE Page'
              trackingId={UIATrackingIds.DISTRIBUTION_ONE_PAGE_OPTION}
            />
          )}

          {isRevealOptionEnabled && (
            <UIADistributeOption
              description='Make every piece of content a product showcase'
              handleClick={(_e) => launchToView('Productize', 'CreateReveal')}
              imageSrc={RevealImg}
              title='Reveal'
              trackingId={UIATrackingIds.DISTRIBUTION_REVEAL_OPTION}
            />
          )}

          {isShowroomOptionEnabled && (
            <UIADistributeOption
              description='Seamlessly integrate commerce into any digital channel'
              handleClick={(_e) => launchShowroomWorkflow()}
              imageSrc={ShowroomImg}
              title='Showroom'
              trackingId={UIATrackingIds.DISTRIBUTION_SHOWROOM_OPTION}
            />
          )}

          {isMediaLibraryOptionEnabled && (
            <UIADistributeOption
              description='Manage all your media in one place to make it shoppable'
              handleClick={(e) => addToLibrary(e)}
              imageSrc={AddToLibraryImg}
              title='Add to Media Library'
              trackingId={UIATrackingIds.DISTRIBUTION_MEDIA_LIBRARY_OPTION}
            />
          )}
        </List>
      )}
    </section>
  );
};

interface UIADistributeOptionProps {
  description: string;
  handleClick: (e: React.MouseEvent) => void;
  imageSrc: string;
  title: string;
  trackingId: TrackingId;
}

function UIADistributeOption({
  description,
  handleClick,
  imageSrc,
  title,
  trackingId,
}: UIADistributeOptionProps) {
  return (
    <ListItem>
      <WorkflowButton onClick={handleClick} trackingId={trackingId}>
        <Image alt='' src={imageSrc} />
        <TextContainer>
          <Title>{title}</Title>
          <BodySecondaryText>{description}</BodySecondaryText>
        </TextContainer>
      </WorkflowButton>
    </ListItem>
  );
}

const SpinnerContainer = styled.div(
  ({ theme }) => `
  margin-top: ${theme.spacings[7]};
  
`,
);
const List = styled.ul(
  ({ theme }) => `
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: ${theme.colors.white};
  `,
);

const ListItem = styled.li(
  ({ theme }) => `
  display: flex;

  &:not(:first-child) {
    border-top: 1px solid ${theme.colors.gray1};
  }
  `,
);

const WorkflowButton = withTracking(
  styled.button(
    ({ theme }) => `
  display: flex;
  align-items: center;
  width: 100%;
  margin: ${theme.spacings[3]};
  padding: ${theme.spacings[3]};
  border: none;
  background-color: ${theme.colors.white};
    
  &:hover {
    background-color: ${theme.colors.gray00};
  }
`,
  ),
);

const Image = styled.img(
  ({ theme }) => `
  width: calc(${theme.spacings[2]} * 9);
  height: calc(${theme.spacings[2]} * 9);
  `,
);

const TextContainer = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  margin-left: ${theme.spacings[3]};
  text-align: left;

  /* This is meant to be custom styling for UIA only*/
  && *{
    font-weight: 300;
  }
`,
);

const Title = styled(SectionText)(
  ({ theme }) => `
    /* This is meant to be custom styling for UIA only*/
    color: ${theme.colors.teal1};
`,
);

// Register the Angular component that will use our React component
angular.module('uia').component('uiaDistribute', {
  bindings: {
    activateWorkflow: '&',
    addToLibrary: '&',
    isApproveForFanreelOptionEnabled: '<',
    isApproveForGalleriesOptionEnabled: '<',
    isApproveForSyndicationOptionEnabled: '<',
    isMediaLibraryOptionEnabled: '<',
    isOnePageOptionEnabled: '<',
    isRevealOptionEnabled: '<',
    isShowroomOptionEnabled: '<',
    isSocialMediaOptionEnabled: '<',
    launchOnePageWorkflow: '&',
    launchShowroomWorkflow: '&',
    launchToView: '&',
    outstandingAsyncAction: '<',
    fanreels: '<',
  },
  controller: makeAngularReactControllerWithScope(
    (scope: { $ctrl: UIADistributeProps } & IScope) => {
      return (
        <UIADistribute
          activateWorkflow={(workflow) =>
            applyScope(scope, scope.$ctrl.activateWorkflow)({ workflow })
          }
          addToLibrary={(e) =>
            applyScope(scope, scope.$ctrl.addToLibrary)({ $event: e })
          }
          isApproveForFanreelOptionEnabled={
            scope.$ctrl.isApproveForFanreelOptionEnabled
          }
          isApproveForGalleriesOptionEnabled={
            scope.$ctrl.isApproveForGalleriesOptionEnabled
          }
          isApproveForSyndicationOptionEnabled={
            scope.$ctrl.isApproveForSyndicationOptionEnabled
          }
          isMediaLibraryOptionEnabled={scope.$ctrl.isMediaLibraryOptionEnabled}
          isOnePageOptionEnabled={scope.$ctrl.isOnePageOptionEnabled}
          isRevealOptionEnabled={scope.$ctrl.isRevealOptionEnabled}
          isShowroomOptionEnabled={scope.$ctrl.isShowroomOptionEnabled}
          isSocialMediaOptionEnabled={scope.$ctrl.isSocialMediaOptionEnabled}
          launchOnePageWorkflow={() =>
            applyScope(scope, scope.$ctrl.launchOnePageWorkflow)()
          }
          launchShowroomWorkflow={() =>
            applyScope(scope, scope.$ctrl.launchShowroomWorkflow)()
          }
          launchToView={(view, workflow) =>
            applyScope(scope, scope.$ctrl.launchToView)({ view, workflow })
          }
          outstandingAsyncAction={scope.$ctrl.outstandingAsyncAction}
        />
      );
    },
  ),
});
